/**
 * Tracker Postback — top-level menu page that owns the WHOLE
 * postback experience for every landing page the operator manages:
 *
 *   • Inbound S2S (partner site → us → Meta CAPI)
 *   • Outbound tracker postbacks (us → Voluum / RedTrack / Binom /
 *     FunnelFlux / Bemob / custom)
 *   • Recent inbound + outbound dispatch logs
 *
 * Why surface this at the top level (not just inside the Builder):
 *   - Operators rarely want to open the Builder just to copy a postback
 *     URL or check whether tonight's traffic is firing back. A
 *     dedicated page lets them park it on its own browser tab.
 *   - Conversion / tracking infra in HitzLink is already promoted to
 *     top-level pages (Pixel Manager, Meta CAPI). Postback rounds out
 *     that group.
 *
 * Layout: left rail with the operator's pages + status badges,
 * right pane is the existing AffiliatePostbackPanel for the selected
 * page. The panel itself owns its data fetching + writes — this page
 * just adds the per-page selector + at-a-glance "is it on" badges.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Send,
  Lock,
  Search,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Plus,
  Inbox,
  RefreshCcw,
} from 'lucide-react';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { useEffectivePlan } from '../store/accountStore';
import { hasFeature } from '../constants/planLimits';
import { AffiliatePostbackPanel } from '../components/Common/AffiliatePostbackPanel';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface PageRow {
  id: string;
  name: string;
  slug: string;
}

interface PageStatus {
  inboundEnabled: boolean;
  outboundEnabled: boolean;
  outboundPreset: string | null;
  loaded: boolean;
}

type StatusMap = Record<string, PageStatus>;

// Hard-cap concurrent fan-out. Two requests per page (inbound +
// outbound) on a 50-page operator would otherwise blast 100 sockets at
// once and trigger Cloudflare per-IP rate limits long before the API
// itself complained. 10 is fast enough that 50 pages finish in well
// under a second on a warm cache and slow enough that nothing rate-
// limits.
const FAN_OUT_LIMIT = 10;

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().token || localStorage.getItem('token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchInboundConfig(pageId: string): Promise<{
  enabled: boolean;
  postbackSecret: string | null;
} | null> {
  try {
    const res = await fetch(`${API_URL}/capi/affiliate-config/${pageId}`, {
      headers: authHeader(),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      enabled: data?.enabled !== false,
      postbackSecret: data?.postbackSecret ?? null,
    };
  } catch {
    return null;
  }
}

async function fetchOutboundConfig(pageId: string): Promise<{
  enabled: boolean;
  preset: string;
} | null> {
  try {
    const res = await fetch(`${API_URL}/capi/outbound-config/${pageId}`, {
      headers: authHeader(),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const out = data?.outbound;
    if (!out) return null;
    return {
      enabled: out?.enabled === true,
      preset: out?.preset || 'custom',
    };
  } catch {
    return null;
  }
}

/**
 * Bounded-concurrency fan-out. Plain Promise.all blows the per-IP
 * connection budget on large operators; this keeps at most
 * `FAN_OUT_LIMIT` sockets in flight at any moment.
 */
async function loadStatusesBounded(
  pageIds: string[],
  onProgress: (pageId: string, status: PageStatus) => void,
): Promise<void> {
  let cursor = 0;
  async function worker() {
    while (true) {
      const i = cursor++;
      if (i >= pageIds.length) return;
      const pageId = pageIds[i];
      const [inbound, outbound] = await Promise.all([
        fetchInboundConfig(pageId),
        fetchOutboundConfig(pageId),
      ]);
      onProgress(pageId, {
        inboundEnabled:
          inbound !== null && inbound.enabled && !!inbound.postbackSecret,
        outboundEnabled: outbound?.enabled === true,
        outboundPreset: outbound?.preset || null,
        loaded: true,
      });
    }
  }
  const workers = Array.from(
    { length: Math.min(FAN_OUT_LIMIT, pageIds.length) },
    () => worker(),
  );
  await Promise.allSettled(workers);
}

export const TrackerPostback: React.FC = () => {
  const { t } = useTranslation('tracking');
  const navigate = useNavigate();
  const effectivePlan = useEffectivePlan();

  const hasAccess = hasFeature(effectivePlan, 'affiliatePostback');

  // ── Locked surface for tiers without `affiliatePostback` ──────────
  // We render INSIDE the UserLayout so the sidebar stays mounted —
  // dropping the user on a different layout would feel like a 404.
  // Server-side gates (requireFeature middleware on every /capi/*
  // endpoint) are the real defense; this is the conversion surface.
  if (!hasAccess) {
    return (
      <UserLayout>
        <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500/15 via-pink-500/15 to-rose-500/15 ring-1 ring-purple-500/20">
                <Lock className="h-7 w-7 text-purple-600" strokeWidth={2.25} />
              </div>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {t('tracker.title')}
                  </h1>
                  <span className="rounded-full bg-gradient-to-r from-purple-500 to-pink-600 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                    Pro
                  </span>
                </div>
                <p className="mb-4 text-sm text-gray-600">
                  {t('tracker.lockedDesc')}
                </p>
                <ul className="mb-5 space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('tracker.lockedBenefits.inbound')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('tracker.lockedBenefits.outbound')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('tracker.lockedBenefits.log')}</span>
                  </li>
                </ul>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 transition-all"
                  >
                    {t('tracker.lockedCta')}
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    {t('tracker.lockedBack')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <TrackerPostbackInner />
    </UserLayout>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Inner — only rendered when the user has feature access. Keeps the
//  paid surface from issuing any /pages or /capi/* requests at all
//  for free users (no wasted bandwidth, no spurious 403s in the
//  console).
// ────────────────────────────────────────────────────────────────────

const TrackerPostbackInner: React.FC = () => {
  const { t } = useTranslation('tracking');
  const navigate = useNavigate();

  const [pages, setPages] = useState<PageRow[]>([]);
  const [statuses, setStatuses] = useState<StatusMap>({});
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [pagesError, setPagesError] = useState<string | null>(null);

  async function loadPages() {
    try {
      setLoadingPages(true);
      setPagesError(null);
      // ?fast=1 returns only metadata — we don't need analytics
      // counts here, just id/name/slug for the picker.
      const res = await fetch(`${API_URL}/pages?fast=1`, {
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const rows: PageRow[] = (raw || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        slug: p.slug || '',
      }));
      setPages(rows);
      if (rows.length > 0) {
        // Default-select the first page so the right pane has
        // something to render — operators land on this page wanting
        // to see something, not an empty state.
        setSelectedPageId((cur) => cur ?? rows[0].id);
      }
      void loadStatuses(rows.map((r) => r.id));
    } catch (err) {
      setPagesError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingPages(false);
    }
  }

  async function loadStatuses(ids: string[]) {
    if (ids.length === 0) return;
    setLoadingStatuses(true);
    try {
      await loadStatusesBounded(ids, (pageId, status) => {
        // Stream results in as workers finish so badges pop in
        // progressively rather than waiting for the full batch.
        setStatuses((prev) => ({ ...prev, [pageId]: status }));
      });
    } finally {
      setLoadingStatuses(false);
    }
  }

  async function refreshStatusFor(pageId: string) {
    const [inbound, outbound] = await Promise.all([
      fetchInboundConfig(pageId),
      fetchOutboundConfig(pageId),
    ]);
    setStatuses((prev) => ({
      ...prev,
      [pageId]: {
        inboundEnabled:
          inbound !== null && inbound.enabled && !!inbound.postbackSecret,
        outboundEnabled: outbound?.enabled === true,
        outboundPreset: outbound?.preset || null,
        loaded: true,
      },
    }));
  }

  useEffect(() => {
    void loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q),
    );
  }, [pages, search]);

  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedPageId) || null,
    [pages, selectedPageId],
  );

  function handleSelect(pageId: string) {
    setSelectedPageId(pageId);
    // Refresh the badge for the page they just opened — ensures the
    // dot stays in sync if they toggle the panel's enable switch and
    // come back to the rail.
    void refreshStatusFor(pageId);
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Send className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">
              {t('tracker.title')}
            </h1>
            <p className="text-sm text-gray-600">{t('tracker.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => loadPages()}
            disabled={loadingPages}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            title={t('tracker.refreshTitle')}
          >
            <RefreshCcw
              className={`w-4 h-4 ${loadingPages ? 'animate-spin' : ''}`}
            />
            <span className="hidden md:inline">{t('tracker.refresh')}</span>
          </button>
        </div>
      </header>

      {pagesError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {t('tracker.loadFailed')}: {pagesError}
        </div>
      )}

      {/* No pages: blank-slate full-width */}
      {!loadingPages && !pagesError && pages.length === 0 ? (
        <NoPagesEmptyState onCreate={() => navigate('/dashboard')} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4">
          {/* Left rail — page list */}
          <aside className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col min-h-[300px] lg:max-h-[calc(100vh-180px)]">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('tracker.searchPlaceholder')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                <span>
                  {t('tracker.pageCount', {
                    shown: filteredPages.length,
                    total: pages.length,
                  })}
                </span>
                {loadingStatuses && (
                  <span className="inline-flex items-center gap-1 text-gray-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {t('tracker.statusLoading')}
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingPages ? (
                <div className="flex items-center justify-center py-10 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('tracker.loadingPages')}
                </div>
              ) : filteredPages.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('tracker.noMatches')}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {filteredPages.map((p) => {
                    const status = statuses[p.id];
                    const isSelected = selectedPageId === p.id;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(p.id)}
                          className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors ${
                            isSelected
                              ? 'bg-purple-50 border-l-2 border-purple-500'
                              : 'border-l-2 border-transparent'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <StatusDot status={status} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {p.name || t('tracker.unnamedPage')}
                              </div>
                              {p.slug && (
                                <code className="text-[11px] text-gray-500 truncate block">
                                  /{p.slug}
                                </code>
                              )}
                              <StatusBadges status={status} />
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* Right pane — the panel for the selected page */}
          <section className="min-w-0">
            {selectedPage ? (
              <div className="bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-xl p-6 shadow-sm">
                {/* Page-context strip — gives the operator a visible
                    confirmation of WHICH page they're configuring,
                    and a one-click bounce to the Builder for that
                    page's other settings. */}
                <div className="flex items-center justify-between gap-3 mb-5 pb-4 border-b border-gray-800">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-purple-300/80">
                      {t('tracker.contextLabel')}
                    </div>
                    <div className="text-base font-semibold text-white truncate">
                      {selectedPage.name || t('tracker.unnamedPage')}
                    </div>
                    {selectedPage.slug && (
                      <code className="text-xs text-gray-400 truncate block">
                        /{selectedPage.slug}
                      </code>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/builder?pageId=${selectedPage.id}`)
                    }
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-200 hover:text-white bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-lg transition-colors"
                  >
                    {t('tracker.openBuilder')}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>

                {/* The panel — reused as-is, standalone (non-embedded)
                    framing so it shows its own header + description. */}
                <AffiliatePostbackPanel pageId={selectedPage.id} />
              </div>
            ) : (
              <SelectPagePrompt />
            )}
          </section>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Status visuals — extracted so the page list rows stay readable
// ────────────────────────────────────────────────────────────────────

const StatusDot: React.FC<{ status?: PageStatus }> = ({ status }) => {
  const color = !status?.loaded
    ? 'bg-gray-300'
    : status.inboundEnabled || status.outboundEnabled
    ? 'bg-emerald-500'
    : 'bg-gray-300';
  return (
    <span
      className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${color}`}
      aria-hidden="true"
    />
  );
};

const StatusBadges: React.FC<{ status?: PageStatus }> = ({ status }) => {
  const { t } = useTranslation('tracking');
  if (!status?.loaded) return null;
  if (!status.inboundEnabled && !status.outboundEnabled) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {status.inboundEnabled && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
          <Inbox className="w-2.5 h-2.5" />
          {t('tracker.badge.inbound')}
        </span>
      )}
      {status.outboundEnabled && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 text-blue-700 border border-blue-200">
          <Send className="w-2.5 h-2.5" />
          {status.outboundPreset
            ? t('tracker.badge.outboundWithPreset', {
                preset: prettifyPreset(status.outboundPreset),
              })
            : t('tracker.badge.outbound')}
        </span>
      )}
    </div>
  );
};

function prettifyPreset(preset: string): string {
  // Display the preset name in the same casing the tracker vendors
  // use (e.g. "Voluum", "RedTrack") — looks more polished than the
  // raw lowercase id we store in JSON.
  switch (preset) {
    case 'voluum':
      return 'Voluum';
    case 'redtrack':
      return 'RedTrack';
    case 'binom':
      return 'Binom';
    case 'funnelflux':
      return 'FunnelFlux';
    case 'bemob':
      return 'BeMob';
    case 'custom':
      return 'Custom';
    default:
      return preset.charAt(0).toUpperCase() + preset.slice(1);
  }
}

// ────────────────────────────────────────────────────────────────────
//  Empty / prompt states
// ────────────────────────────────────────────────────────────────────

const NoPagesEmptyState: React.FC<{ onCreate: () => void }> = ({
  onCreate,
}) => {
  const { t } = useTranslation('tracking');
  return (
    <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-50 flex items-center justify-center">
        <Send className="w-8 h-8 text-purple-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {t('tracker.empty.title')}
      </h3>
      <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">
        {t('tracker.empty.body')}
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
      >
        <Plus className="w-4 h-4" />
        {t('tracker.empty.cta')}
      </button>
    </div>
  );
};

const SelectPagePrompt: React.FC = () => {
  const { t } = useTranslation('tracking');
  return (
    <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-10 flex items-center justify-center min-h-[300px]">
      <div className="text-center max-w-sm">
        <div className="hidden lg:flex items-center justify-center mb-3 text-gray-400">
          <ArrowLeft className="w-5 h-5 mr-1" />
          <span className="text-xs uppercase tracking-wider">
            {t('tracker.selectArrow')}
          </span>
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {t('tracker.selectPrompt.title')}
        </h3>
        <p className="text-sm text-gray-600">
          {t('tracker.selectPrompt.body')}
        </p>
      </div>
    </div>
  );
};

export default TrackerPostback;
